// Controls whose operating effectiveness hasn't been started. Nothing supports the period yet, so
// the only thing to do on the page is draft an argument — which is what the plan wizard is for.
//
// Titles and taglines track the design document so a walkthrough can follow it section by section.
import {Conclusion, DemoControl, Scenario} from "../model";
import {
  CAST_EDGES,
  SAP,
  batchJobs,
  changeManagement,
  creditLimit,
  customerMasterReview,
  duplicateInvoice,
  effective,
  goodsReceiptReview,
  logicalAccess,
  logicalAccessFailedAtSap,
  payrollChangeApproval,
  payrollReconciliation,
  privilegedAccess,
  revenueCutOff,
  threeWayMatch,
} from "./cast";

const FY26 = {from: "2026-01-01", to: "2026-12-31"};

const unstarted: Conclusion = {outcome: "insufficient_evidence", supportsReliance: false, justification: ""};

const creditLimitConclusion = effective(
  "The application rejects an order taking a customer above its approved credit limit. Relied upon on " +
    "the basis of the SAP ITGCs.",
);

/** The two-layer cast, with the control being planned stripped back to no conclusion of its own. */
const cast = (focus: DemoControl, access: DemoControl = logicalAccess): DemoControl[] =>
  [
    privilegedAccess,
    changeManagement,
    access,
    batchJobs,
    threeWayMatch(),
    duplicateInvoice,
    goodsReceiptReview,
    creditLimit(creditLimitConclusion),
    customerMasterReview,
    revenueCutOff,
    payrollChangeApproval,
    payrollReconciliation,
  ].map(control => (control.ref === focus.ref ? focus : control));

const unstartedThreeWayMatch: DemoControl = {...threeWayMatch(), frequency: "daily"};

export const planningAutomated: Scenario = {
  id: "s8",
  section: "Scenario 8",
  title: "Planning an automated control",
  tagline: "the plan is the basis, drafted",
  situation:
    "VI-02 has concluded effective on design and implementation, and nothing has been done on " +
    "operating effectiveness. The wizard reads the control, sees the application performs it, and " +
    "drafts the argument that the SAP ITGCs extend a single observation across the year.",
  focus: {control: "VI-02", scope: "Global"},
  controls: cast(unstartedThreeWayMatch),
  edges: CAST_EDGES,
  oe: [{control: "VI-02", scope: "Global", period: FY26, basis: [], conclusion: unstarted}],
};

/** Manual, but the reviewer reads a report SAP produces — so it leans on the same ITGCs. */
const unstartedGoodsReceiptReview: DemoControl = {
  ...goodsReceiptReview,
  frequency: "monthly",
  scopes: [{scope: "Global", design: "effective", implementation: "effective"}],
};

export const planningWithFailedItgc: Scenario = {
  id: "s9",
  section: "Scenario 9",
  title: "Planning where the plan finds a problem",
  tagline: "the reliance check, before any fieldwork",
  situation:
    "VI-06 is a manual review of a report SAP produces, so the library template leans on the SAP " +
    "ITGCs. Logical access has already been concluded not effective at SAP — which the configuration " +
    "says at the point it is generated, not in November. Switching to a template that tests the " +
    "control directly closes the period again.",
  focus: {control: "VI-06", scope: "Global"},
  controls: cast(unstartedGoodsReceiptReview, logicalAccessFailedAtSap),
  edges: CAST_EDGES.filter(edge => edge.to !== "VI-06" || edge.scope === SAP),
  oe: [{control: "VI-06", scope: "Global", period: FY26, basis: [], conclusion: unstarted}],
};
